import { useRef, useState } from 'react';
import { AwsClientOverrides, useAwsEffect, useSnackbar } from '@localstack/services';
import { GetWebFunctionRevisionResponse, LambdaWebRevisionFormValues } from '@localstack/types';

interface WebRevisionCreateResult {
  create: (
    functionName: string,
    revision: GetWebFunctionRevisionResponse,
    data: LambdaWebRevisionFormValues,
  ) => Promise<string | undefined>;
  isLoading: boolean;
}

export const useWebRevisionCreate = (clientOverrides?: AwsClientOverrides): WebRevisionCreateResult => {
  const { showSnackbar } = useSnackbar();
  const submitting = useRef(false);
  const [readingFile, setReadingFile] = useState(false);
  const { putObject, isLoading: uploading } = useAwsEffect('S3', ['putObject'], {
    clientOverrides: { s3ForcePathStyle: true, ...clientOverrides },
  });
  const { createWebFunctionRevision, isLoading: creating } = useAwsEffect('LambdaWeb', ['createWebFunctionRevision'], {
    clientOverrides,
    revalidate: ['getWebFunction', 'listWebFunctionRevisions', 'listWebFunctionEndpoints'],
  });

  const create: WebRevisionCreateResult['create'] = async (functionName, revision, data) => {
    const source = revision.buildConfig?.codeConfig?.s3Object;
    const roleArn = revision.serviceConfig?.executionRoleArn;
    if (submitting.current || !source?.bucket || !source.key || !roleArn || !revision.buildConfig) return undefined;
    submitting.current = true;
    try {
      let codeSource = source;
      if (data.codeSource === 's3') {
        if (!data.sourceBucket || !data.sourceKey) return undefined;
        codeSource = {
          bucket: data.sourceBucket,
          key: data.sourceKey,
          ...(data.sourceVersion && { versionId: data.sourceVersion }),
        };
      } else {
        if (!data.zipFile) return undefined;
        let body: Uint8Array;
        setReadingFile(true);
        try {
          body = new Uint8Array(await data.zipFile.arrayBuffer());
        } catch {
          showSnackbar({ severity: 'error', message: 'Could not read the ZIP file. Select it again and retry.' });
          return undefined;
        } finally {
          setReadingFile(false);
        }
        // Never replace a previous revision's object or change a user's bucket configuration.
        const directory = source.key.slice(0, source.key.lastIndexOf('/') + 1);
        const key = `${directory}${crypto.randomUUID()}.zip`;
        const uploaded = await putObject({
          Bucket: source.bucket,
          Key: key,
          Body: body,
          ContentType: 'application/zip',
        });
        if (!uploaded) return undefined;
        codeSource = {
          bucket: source.bucket,
          key,
          ...(uploaded.VersionId && uploaded.VersionId !== 'null' && { versionId: uploaded.VersionId }),
        };
      }
      const created = await createWebFunctionRevision({
        functionName,
        description: data.description || undefined,
        kmsKeyArn: revision.kmsKeyArn,
        buildConfig: {
          ...revision.buildConfig,
          codeConfig: { ...revision.buildConfig.codeConfig, s3Object: codeSource },
        },
        serviceConfig: { ...revision.serviceConfig, executionRoleArn: roleArn },
      });
      return created?.revisionId;
    } finally {
      submitting.current = false;
    }
  };
  return { create, isLoading: readingFile || uploading || creating };
};
